import { createStore } from "vuex";

// JWT Decode
import { jwtDecode } from "jwt-decode";
import { emitter } from "@/services/emitter";
import { watch } from "vue";
import { createSettingsSaveOwner } from "@/services/settingsSaveOwner";
import { getAPIErrorStatus, isMutationOutcomeUnconfirmed } from "@/services/apiErrors";
import { chatAppActive } from "@/services/chatActivity";

// `API` is wired in by `initializeStoreSideEffects(apiInstance)` from
// main.js once the singleton is constructed. Importing `API` at module-load
// here would re-introduce the `@/main` ↔ `@/store` cycle that previously
// triggered a TDZ during HMR. Actions reference this variable at runtime.
let API;
let settingsSaveOwner;
let disposeStoreSideEffects;
let adoptingSettings = false;

// Feature Flags
import { Flags, hasFeatureFlag, getRealFlagValue } from "@/classes/FeatureFlags";

import { Capacitor } from "@capacitor/core";
import { setCustomerIDInStorage } from "./classes/MobileStorage";
import { normalizeCustomerID } from "@/classes/customerID";
import { clearSessionDevOverrides } from "@/classes/devOverrides";
import { planPreviewOverrides, restorePlanPreview } from "@/classes/customerPlans";
import { MOBILE_BREAKPOINT_PX } from "./classes/constants";

// Toasts
import { createPlainTextToaster } from "@/services/toasts";

// Sentry
import * as Sentry from "@sentry/vue";

// Product analytics
import posthog from "posthog-js";

const posthogConfigured = Boolean(
    import.meta.env.VITE_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_POSTHOG_HOST
);

export function jsonValuesEqual(left, right) {
    if (left === right) return true;
    if (left === null || right === null || typeof left !== "object" || typeof right !== "object") {
        return false;
    }

    if (Array.isArray(left) !== Array.isArray(right)) return false;

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;

    return leftKeys.every(
        (key) =>
            Object.prototype.hasOwnProperty.call(right, key) &&
            jsonValuesEqual(left[key], right[key])
    );
}

// Currency configuration - comprehensive list
export const CURRENCY_CONFIG = {
    // Europe
    GBP: { code: "GBP", symbol: "£", locale: "en-GB", name: "British Pound" },
    EUR: { code: "EUR", symbol: "€", locale: "de-DE", name: "Euro" },
    CHF: { code: "CHF", symbol: "CHF", locale: "de-CH", name: "Swiss Franc" },

    // Americas
    USD: { code: "USD", symbol: "$", locale: "en-US", name: "US Dollar" },
    CAD: { code: "CAD", symbol: "C$", locale: "en-CA", name: "Canadian Dollar" },

    // Asia-Pacific
    AUD: { code: "AUD", symbol: "A$", locale: "en-AU", name: "Australian Dollar" },
    NZD: { code: "NZD", symbol: "NZ$", locale: "en-NZ", name: "New Zealand Dollar" },
    SGD: { code: "SGD", symbol: "S$", locale: "en-SG", name: "Singapore Dollar" },
    HKD: { code: "HKD", symbol: "HK$", locale: "en-HK", name: "Hong Kong Dollar" },
    JPY: { code: "JPY", symbol: "¥", locale: "ja-JP", name: "Japanese Yen", decimals: 0 },
    INR: { code: "INR", symbol: "₹", locale: "en-IN", name: "Indian Rupee" },

    // Africa
    TZS: { code: "TZS", symbol: "TSh", locale: "sw-TZ", name: "Tanzanian Shilling", decimals: 0 },
    ZAR: { code: "ZAR", symbol: "R", locale: "en-ZA", name: "South African Rand" },
    KES: { code: "KES", symbol: "KSh", locale: "en-KE", name: "Kenyan Shilling" },

    // Middle East
    AED: { code: "AED", symbol: "د.إ", locale: "ar-AE", name: "UAE Dirham" },
    SAR: { code: "SAR", symbol: "﷼", locale: "ar-SA", name: "Saudi Riyal" }
};
export const DEFAULT_CURRENCY = "GBP";

// Resolves once App.vue's created() bootstrap finishes — regardless of
// whether auth succeeded. The router guard awaits this so it doesn't race
// the bootstrap, then runs its own checkAuthWithoutLogout() to decide
// where to send the user. "Ready" means "bootstrap settled," not "logged in".
let resolveAppReady;
export const appReady = new Promise((resolve) => {
    resolveAppReady = resolve;
});
export function markAppReady() {
    resolveAppReady();
}

// store
export const store = createStore({
    state: {
        client: null,
        departmentID: "",
        department: null,
        departmentList: [],
        locationID: "",
        location: null,
        locationList: [],
        supplierList: [],
        checkFormList: [],
        temperatureLocationList: [],
        stockList: [],
        recipeList: [],
        userList: [],
        haccpPlan: null,
        settings: {},
        user: null,
        os: Capacitor.getPlatform(),
        windowWidth: typeof window !== "undefined" ? window.innerWidth : 1920,
        isMobile: typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT_PX : false,
        isMobileOS: Capacitor.isNativePlatform(),
        token: null,
        // Changes whenever authentication changes. Async tenant-data loads capture
        // this value so a response from the previous user cannot repopulate the
        // store after logout (or after a fast logout -> login as someone else).
        authSessionRevision: 0,
        authUnlockRequired: false,
        isRouteLoading: false,
        customerID: "",
        $toast: null,
        settingsUpdateInProgress: false,
        settingsSaving: false,
        settingsDirty: false,
        settingsSaveError: "",
        settingsRevision: 0,
        sidebarCollapsed: (() => {
            if (typeof window === "undefined") return false;
            try {
                return window.localStorage.getItem("sidebar.collapsed") === "1";
            } catch (e) {
                return false;
            }
        })(),
        sidebarOpen: false,
        commandPaletteOpen: false,
        devOverrides: {
            active: false,
            planPreviewID: null,
            roleOverride: null,
            flagOverrides: {},
            panelOpen: false,
            // Dev-only: impersonate a tenant locally (localhost/staging). When set,
            // it overrides the Customer-ID derived from the subdomain so local code
            // runs against a tenant whose data was loaded via load-tenant.sh.
            tenantOverride: null
        }
    },
    strict: import.meta.env?.DEV === true,
    mutations: {
        setSettings(state, settings = {}) {
            state.settings = JSON.parse(JSON.stringify(settings || {}));
        },
        patchSettings(state, patch) {
            state.settings = {
                ...(state.settings || {}),
                ...(patch || {})
            };
        },
        setSetupCompleted(state, completed) {
            const setupCompleted = !!completed;
            state.settings = {
                ...(state.settings || {}),
                setup_completed: setupCompleted
            };
            if (state.client) {
                state.client = {
                    ...state.client,
                    settings: {
                        ...(state.client.settings || {}),
                        setup_completed: setupCompleted
                    }
                };
            }
        },
        setUser(state, user) {
            const previousUserId = state.user?.id || state.user?._id;
            const userId = user?.id || user?._id;

            state.user = user;
            if (user) {
                Sentry.setUser({
                    id: userId,
                    email: user.email,
                    username: user.username
                });

                if (userId && previousUserId !== userId && posthogConfigured) {
                    // Easy User Switch changes the active person without a logout.
                    // Start a new PostHog identity before associating future events.
                    if (previousUserId && previousUserId !== userId) {
                        posthog.reset();
                    }

                    const personProperties = {};
                    const email = user.email || user.email_address;
                    if (email) personProperties.email = email;
                    if (state.customerID) personProperties.tenant = state.customerID;
                    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
                    if (name) personProperties.name = name;
                    if (user.username) personProperties.username = user.username;

                    posthog.identify(userId, personProperties);
                }
            } else {
                Sentry.setUser(null);
                if (posthogConfigured) posthog.reset();
            }
        },
        setClient(state, client) {
            state.client = client;
        },
        setHaccpPlan(state, haccpPlan) {
            state.haccpPlan = haccpPlan;
        },
        setDepartmentList(state, departmentList) {
            state.departmentList = departmentList || [];
        },
        setLocationList(state, locationList) {
            state.locationList = locationList || [];
        },
        setSupplierList(state, supplierList) {
            state.supplierList = supplierList || [];
        },
        setCheckFormList(state, checkFormList) {
            state.checkFormList = checkFormList || [];
        },
        setUserList(state, userList) {
            state.userList = userList || [];
        },
        setTemperatureLocationList(state, temperatureLocationList) {
            state.temperatureLocationList = temperatureLocationList || [];
        },
        setStockList(state, stockList) {
            state.stockList = stockList || [];
        },
        setRecipeList(state, recipeList) {
            state.recipeList = recipeList || [];
        },
        setToken(state, token) {
            if (state.token !== token) state.authSessionRevision += 1;
            state.token = token;
        },
        setAuthUnlockRequired(state, required) {
            state.authUnlockRequired = !!required;
        },
        setWindowMetrics(state, width) {
            state.windowWidth = width;
            state.isMobile = width < MOBILE_BREAKPOINT_PX;
        },
        setSettingsSaveState(state, { saving, dirty, error }) {
            state.settingsSaving = saving;
            state.settingsDirty = dirty;
            state.settingsSaveError = error;
        },
        bumpSettingsRevision(state) {
            state.settingsRevision += 1;
        },
        setSettingsUpdateInProgress(state, inProgress) {
            state.settingsUpdateInProgress = !!inProgress;
        },
        setRouteLoading(state, isRouteLoading) {
            state.isRouteLoading = isRouteLoading;
        },
        setCustomerID(state, customerID) {
            const normalizedCustomerID = normalizeCustomerID(customerID);
            setCustomerIDInStorage(normalizedCustomerID);
            state.customerID = normalizedCustomerID;
            Sentry.setTag("tenant", normalizedCustomerID);
        },
        setDepartmentByID(state, departmentID) {
            state.departmentID = departmentID;
            state.department = state.departmentList.find((d) => d.id === departmentID);
            localStorage.setItem("department_id", departmentID);
            emitter.emit("department-change");
        },
        setLocationByID(state, locationID) {
            state.locationID = locationID;
            state.location = state.locationList.find((l) => l.id === locationID);

            // console.log("Location set to", state.location?.name);

            localStorage.setItem("location_id", locationID);
            emitter.emit("location-change");
        },
        setToaster(state, toast) {
            state.$toast = toast;
        },
        setSidebarCollapsed(state, collapsed) {
            state.sidebarCollapsed = !!collapsed;
            try {
                window.localStorage.setItem("sidebar.collapsed", collapsed ? "1" : "0");
            } catch (e) {
                // ignore - localStorage may not be available
            }
        },
        setSidebarOpen(state, open) {
            state.sidebarOpen = !!open;
        },
        setCommandPaletteOpen(state, open) {
            state.commandPaletteOpen = !!open;
        },
        setDevOverridesActive(state, active) {
            state.devOverrides.active = active;
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        setDevRoleOverride(state, role) {
            state.devOverrides.roleOverride = role;
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        setDevFlagOverride(state, { flag, value }) {
            const updated = { ...state.devOverrides.flagOverrides };
            if (value === undefined) {
                delete updated[flag];
            } else {
                updated[flag] = value;
            }
            state.devOverrides.flagOverrides = updated;
            if (state.devOverrides.planPreviewID) state.devOverrides.planPreviewID = "custom";
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        setDevPlanPreview(state, planID) {
            state.devOverrides.flagOverrides = planID
                ? planPreviewOverrides(state.devOverrides.flagOverrides, planID)
                : restorePlanPreview(state.devOverrides.flagOverrides);
            state.devOverrides.planPreviewID = planID || null;
            state.devOverrides.active = true;
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        setDevPanelOpen(state, open) {
            state.devOverrides.panelOpen = open;
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        setDevTenantOverride(state, tenant) {
            state.devOverrides.tenantOverride = tenant || null;
            localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
        },
        restoreDevOverrides(state) {
            try {
                const stored = localStorage.getItem("devOverrides");
                if (stored) {
                    const parsed = JSON.parse(stored);
                    state.devOverrides.active = parsed.active || false;
                    state.devOverrides.roleOverride = parsed.roleOverride || null;
                    state.devOverrides.flagOverrides = parsed.flagOverrides || {};
                    state.devOverrides.planPreviewID = parsed.planPreviewID || null;
                    state.devOverrides.panelOpen = parsed.panelOpen || false;
                    state.devOverrides.tenantOverride = parsed.tenantOverride || null;
                }
            } catch (e) {
                // ignore - localStorage may not be available in test environments
            }
        },
        clearDevOverrides(state) {
            state.devOverrides.active = false;
            state.devOverrides.roleOverride = null;
            state.devOverrides.flagOverrides = {};
            state.devOverrides.planPreviewID = null;
            state.devOverrides.panelOpen = false;
            state.devOverrides.tenantOverride = null;
            localStorage.removeItem("devOverrides");
        },
        clearDevSessionOverrides(state) {
            state.devOverrides = clearSessionDevOverrides(state.devOverrides);
            if (state.devOverrides.tenantOverride) {
                localStorage.setItem("devOverrides", JSON.stringify(state.devOverrides));
            } else {
                localStorage.removeItem("devOverrides");
            }
        },
        // Resets every tenant-scoped slice so a quick logout → re-login (same
        // browser, possibly different tenant) can't briefly show the previous
        // session's lists/client/user before the new tenant's data loads.
        // Intentionally keeps customerID (derived from subdomain on web) and
        // device-level state (isMobile, devOverrides, sidebarCollapsed).
        resetTenantState(state) {
            state.user = null;
            state.client = null;
            state.settings = {};
            state.userList = [];
            state.locationList = [];
            state.departmentList = [];
            state.supplierList = [];
            state.checkFormList = [];
            state.temperatureLocationList = [];
            state.stockList = [];
            state.recipeList = [];
            state.location = null;
            state.locationID = "";
            state.department = null;
            state.departmentID = "";
            state.authUnlockRequired = false;
        }
    },
    actions: {
        saveSettings(_context, options = {}) {
            if (!settingsSaveOwner || !this.state.client || !this.state.token) {
                return Promise.reject({
                    status: 401,
                    message: "Log in again before saving settings."
                });
            }
            return settingsSaveOwner.request({ notify: false, ...options });
        },
        completeSetup() {
            return this.dispatch("saveSettings", { patch: { setup_completed: true } });
        },
        async refreshClient() {
            const context = settingsContext();
            const revision = this.state.settingsRevision;
            const client = await API.Client.Get();
            if (!this.state.token || settingsContext() !== context) return;
            this.commit(
                "setClient",
                revision === this.state.settingsRevision
                    ? client
                    : { ...client, settings: this.state.client?.settings || this.state.settings }
            );
        },
        async refreshDepartmentList() {
            const sessionRevision = this.state.authSessionRevision;
            const departments = await API.Department.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setDepartmentList", departments);
        },
        async refreshLocationList() {
            const sessionRevision = this.state.authSessionRevision;
            const locations = await API.Location.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setLocationList", locations);
        },
        async refreshSupplierList() {
            const sessionRevision = this.state.authSessionRevision;
            const suppliers = await API.Supplier.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setSupplierList", suppliers);
        },
        async refreshCheckFormList() {
            const sessionRevision = this.state.authSessionRevision;
            const checkForms = await API.CheckForm.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setCheckFormList", checkForms);
        },
        async refreshUserList() {
            const sessionRevision = this.state.authSessionRevision;
            const users = await API.User.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setUserList", users);
        },
        async refreshTemperatureLocationList() {
            const sessionRevision = this.state.authSessionRevision;
            const temperatureLocations = await API.TemperatureLocation.List();
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            this.commit("setTemperatureLocationList", temperatureLocations);
        },
        async refreshStockList() {
            const sessionRevision = this.state.authSessionRevision;
            const stockList = (await API.Stock.List()) || [];
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            stockList.sort((a, b) => a.internal_name.localeCompare(b.internal_name));
            this.commit("setStockList", stockList);
        },
        async refreshRecipeList() {
            const sessionRevision = this.state.authSessionRevision;
            const recipeList = (await API.Recipe.List()) || [];
            if (!this.state.token || this.state.authSessionRevision !== sessionRevision) return;
            recipeList.sort((a, b) => (a.updated > b.updated ? -1 : 1));
            this.commit("setRecipeList", recipeList);
        },
        async refreshLocation() {
            const locationID = localStorage.getItem("location_id");

            // This will trigger the location change and update the location in the state because before login the location list is not set
            if (locationID) {
                this.commit("setLocationByID", locationID);
            }
        },
        async refreshDepartment() {
            const departmentID = localStorage.getItem("department_id");

            if (departmentID) {
                this.commit("setDepartmentByID", departmentID);
            }
        },
        async subscribeToLiveUpdates() {
            // Native lifecycle closes the socket in the background and starts a
            // fresh authenticated subscription on resume. Polling still recovers gaps.
            if (
                !this.state.token ||
                this.state.authUnlockRequired ||
                (this.state.isMobileOS && !chatAppActive.value)
            ) return;
            await API.LiveUpdate.SubscribeToLiveUpdates();
        },

        async loadAllData() {
            // Runs in the background after login (App.vue postLoginActions) — it must
            // never reject. allSettled (not all) so one failed/slow list refresh can't
            // tear down the whole hydration, and a single failure on cold boot can't
            // propagate to created()'s catch and trigger a spurious clearAuth/logout.
            // refreshClient is intentionally omitted — client is loaded via
            // feature-flag bootstrap and checkAndHandleTrialExpired.
            const results = await Promise.allSettled([
                this.dispatch("refreshLocationList"),
                this.dispatch("refreshUserList"),
                ...[
                    [Flags.DepartmentsModule, "refreshDepartmentList"],
                    [Flags.SuppliersModule, "refreshSupplierList"],
                    [Flags.ChecksModule, "refreshCheckFormList"],
                    [Flags.TemperatureLogging, "refreshTemperatureLocationList"],
                    [Flags.StockModule, "refreshStockList"],
                    [Flags.RecipesModule, "refreshRecipeList"]
                ].filter(([flag]) => getRealFlagValue(flag)).map(([, action]) => this.dispatch(action))
            ]);
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length > 0) {
                console.error(
                    `loadAllData: ${failed.length} list refresh(es) failed`,
                    failed.map((r) => r.reason)
                );
            }
        },

        initializeToaster() {
            const toaster = createPlainTextToaster({
                position: this.state.isMobile ? "top" : "bottom-right",
                useDefaultCss: false
            });
            this.commit("setToaster", toaster);
        }
    },
    getters: {
        isTokenValid: (state) => () => {
            if (!state.token) return false;
            if (state.token === undefined) return false;
            if (state.token === "undefined") return false;

            let decoded = null;

            try {
                decoded = jwtDecode(state.token);
            } catch (error) {
                // console.log("Error decoding token", error);
                return false;
            }

            // Check if token is expired. JWT exp is expressed in seconds, Date.now() in milliseconds

            if (!decoded.exp) return false;

            if (Math.round(Date.now() / 1000) > decoded.exp) {
                return false;
            }

            if (decoded.domain !== state.customerID) {
                return false;
            }

            // const now = Date.now() / 1000,
            //     remainingTime = decoded.exp - now,
            //     hours = Math.floor(remainingTime / 3600),
            //     minutes = Math.floor((remainingTime % 3600) / 60),
            //     seconds = Math.floor(remainingTime % 60);
            // console.log(
            //     `Token expires in: ${
            //         hours > 0
            //             ? hours + " hour" + (hours > 1 ? "s" : "") + ", "
            //             : ""
            //     }${
            //         minutes > 0
            //             ? minutes + " minute" + (minutes > 1 ? "s" : "") + ", "
            //             : ""
            //     }${seconds} second${seconds !== 1 ? "s" : ""}`
            // );

            return true;
        },

        isTokenExpired: (state) => () => {
            if (!state.token) return true;
            try {
                const decoded = jwtDecode(state.token);
                return Math.round(Date.now() / 1000) > decoded.exp;
            } catch (error) {
                return true;
            }
        },

        userRole: (state) => () => {
            if (state.devOverrides.active && state.devOverrides.roleOverride) {
                return state.devOverrides.roleOverride;
            }
            if (!state.token) return null;
            try {
                const decoded = jwtDecode(state.token);
                return decoded.roles?.[0] || null;
            } catch (error) {
                return null;
            }
        },

        realUserRole: (state) => () => {
            if (!state.token) return null;
            try {
                const decoded = jwtDecode(state.token);
                return decoded.roles?.[0] || null;
            } catch (error) {
                return null;
            }
        },

        temperatureSymbol: (state) => () => {
            if (!state.location) return "ºC";
            return state.location?.temperatures_in_farenheit ? "ºF" : "ºC";
        },

        hasMultipleLocations: (state) => state.locationList.length > 1,

        currencyConfig: (state) => () => {
            const code = state.settings?.currency || DEFAULT_CURRENCY;
            return CURRENCY_CONFIG[code] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
        },
        currencySymbol: (state, getters) => () => getters.currencyConfig().symbol,
        currencyCode: (state, getters) => () => getters.currencyConfig().code,
        currencyLocale: (state, getters) => () => getters.currencyConfig().locale,

        canAccessDevMode: (state, getters) => () => {
            return (
                getters.userID() === import.meta.env.VITE_ADMIN_ID ||
                import.meta.env.MODE === "development" ||
                state.devOverrides.active
            );
        },

        userID: (state) => () => {
            if (hasFeatureFlag(Flags.EasyUserSwitch) && localStorage.getItem("easy_user_id")) {
                return localStorage.getItem("easy_user_id");
            }
            if (!state.token) return null;
            if (state.token === "undefined") return null;
            const decoded = jwtDecode(state.token);
            return decoded.id || null;
        },

        // Raw JWT id, ignoring the EasyUserSwitch override. Needed to detect an
        // admin-override session even while a kiosk has a staff member selected —
        // userID() returns the selected easy_user_id in that case, which would
        // otherwise mask the override token (id "ADMIN OVERRIDE").
        tokenUserID: (state) => () => {
            if (!state.token) return null;
            if (state.token === "undefined") return null;
            const decoded = jwtDecode(state.token);
            return decoded.id || null;
        },

        user: (state) => () => {
            return state.user;
        },

        userFullName: (state) => () => {
            const first = state.user?.first_name;
            const last = state.user?.last_name;
            const name = `${first || ""} ${last || ""}`.trim();
            return name || state.user?.email || "Admin";
        },

        isMobileOS: (state) => () => {
            return state.isMobileOS;
        },

        domain(state) {
            if (!state.token) {
                return null;
            }
            // Use a type assertion to tell TypeScript that the decoded object should have the shape of DecodedJWT
            const decoded = jwtDecode(state.token);
            return decoded.domain || null;
        },

        isRouteLoading(state) {
            return state.isRouteLoading;
        },

        customerID: (state) => () => {
            return state.customerID;
        }
    }
});

// API-dependent side effects are registered after main.js constructs the
// singleton and calls this. Keeping them out of module top-level eliminates
// the load-time TDZ risk on `API` that previously hit during HMR.
export function settingsContext() {
    return JSON.stringify([
        store.getters.domain,
        store.state.customerID,
        store.state.client?.id,
        store.getters.tokenUserID?.(),
        store.getters.userID?.(),
        store.state.user?.id,
        Boolean(store.state.token)
    ]);
}

export function initializeStoreSideEffects(apiInstance) {
    disposeStoreSideEffects?.();
    API = apiInstance;
    const stops = [];
    settingsSaveOwner = createSettingsSaveOwner({
        context: settingsContext,
        settings: () => store.state.settings,
        save: (settings) => API.Client.Update({ settings }),
        equal: jsonValuesEqual,
        revision: () => store.commit("bumpSettingsRevision"),
        status: (state) => store.commit("setSettingsSaveState", state),
        adopt: (client, settings) => {
            adoptingSettings = true;
            try {
                store.commit("setClient", { ...store.state.client, settings: client.settings });
                store.commit("setSettings", settings);
            } finally {
                adoptingSettings = false;
            }
        },
        failure: (error, notify) => {
            const message =
                getAPIErrorStatus(error) === 403
                    ? "You do not have permission to save these settings. Your changes are still shown here."
                    : isMutationOutcomeUnconfirmed(error)
                      ? "Your settings save could not be confirmed. Your changes are still shown here. Retry saving your settings."
                      : "Your settings could not be saved. Check your changes and retry saving.";
            if (notify) store.state.$toast?.error(message);
            return message;
        }
    });
    const mirrorClientSettings = () => {
        if (!store.state.client || settingsSaveOwner.isDirty()) return;
        const settings = store.state.client.settings || {};
        if (jsonValuesEqual(store.state.settings, settings)) return;
        adoptingSettings = true;
        try {
            store.commit("setSettings", settings);
        } finally {
            adoptingSettings = false;
        }
    };
    stops.push(
        watch(
            settingsContext,
            () => {
                settingsSaveOwner.reset();
                mirrorClientSettings();
            },
            { flush: "sync" }
        )
    );
    stops.push(
        watch(
            () => store.state.locationList,
            (list) => {
                if (list.length > 0) store.dispatch("refreshLocation");
            },
            { deep: true }
        )
    );
    stops.push(
        watch(
            () => store.state.departmentList,
            (list) => {
                if (list.length > 0) store.dispatch("refreshDepartment");
            },
            { deep: true }
        )
    );
    stops.push(
        watch(() => store.state.client, mirrorClientSettings, { deep: true, flush: "sync" })
    );
    const persistEditedSettings = () => {
        if (
            adoptingSettings ||
            store.state.settingsUpdateInProgress ||
            !store.state.client ||
            !store.state.token
        )
            return;
        if (
            jsonValuesEqual(store.state.settings, store.state.client.settings || {}) &&
            !settingsSaveOwner.isDirty()
        )
            return;
        void settingsSaveOwner.request().catch(() => {
            /* The owner retains the draft and error. */
        });
    };
    stops.push(
        watch(() => store.state.settings, persistEditedSettings, { deep: true, flush: "sync" })
    );
    stops.push(
        watch(
            () => store.state.settingsUpdateInProgress,
            (busy) => {
                if (!busy) persistEditedSettings();
            },
            { flush: "sync" }
        )
    );
    mirrorClientSettings();
    disposeStoreSideEffects = () => {
        for (const stop of stops) stop();
        settingsSaveOwner.reset();
    };
    return disposeStoreSideEffects;
}

// Listen for window resize events — safe at module-load (no `API` access).
if (typeof window !== "undefined") {
    window.addEventListener("resize", () => {
        store.commit("setWindowMetrics", window.innerWidth);
    });
}

// Initialize toaster — safe at module-load (no `API` access).
store.dispatch("initializeToaster");
